import { getRouteApi } from '@tanstack/react-router'
import { z } from 'zod'

import { StoryWizard } from '@/features/story/StoryWizard'
import { STORY_TOPIC_IDS } from '@/api/stories'
import { StoryGenerationMode } from '@/schema/settings'

/**
 * `/stories/new?cast=&world=&band=&idea=` — the story wizard.
 *
 * `cast` pre-picks puppets: comma-separated cast uids (`skel:<ULID>` or a catalog uid such as
 * `char:animals/forest/fox`), e.g. the "Use in a story" button sends `?cast=skel:01J…`.
 */
const lenient = <T extends z.ZodTypeAny>(schema: T) => schema.optional().catch(undefined)
export const AGE_BANDS = ['3-4', '5-6', '7-8'] as const
export type AgeBand = (typeof AGE_BANDS)[number]
export const storyNewSearchSchema = z.object({
  cast: lenient(z.string().max(600)),
  world: lenient(z.string().max(60)),
  band: lenient(z.enum(AGE_BANDS)),
  idea: lenient(z.string().max(1200)),
  preset: lenient(z.enum(['parent', 'creator'])),
  generation_mode: lenient(StoryGenerationMode),
  authoring_version: lenient(z.literal('events@1')),
  topic: lenient(z.enum(STORY_TOPIC_IDS)),
  learning_subject: lenient(z.string().trim().min(1).max(80)),
  learning_objective_id: lenient(z.string().min(1).max(100)),
  learning_objective_revision: lenient(z.coerce.number().int().positive()),
})
export type StoryNewSearch = z.infer<typeof storyNewSearchSchema>
export function validateStoryNewSearch(input: Record<string, unknown>): StoryNewSearch {
  return storyNewSearchSchema.parse(input)
}

/** `?cast=` → at most 3 distinct, well-formed uids. */
export function parseCastParam(cast: string | undefined): string[] {
  if (!cast) return []
  const out: string[] = []
  for (const raw of cast.split(',')) {
    const uid = raw.trim()
    if (/^[a-z]+:[^\s,]+$/i.test(uid) && !out.includes(uid)) out.push(uid)
    if (out.length === 3) break
  }
  return out
}

const route = getRouteApi('/stories/new')
export function StoryNewPage() {
  const search = route.useSearch()
  // `topic` / `learning_*` links from before free stories are still read (and ignored): every story is free now
  return <StoryWizard initialCast={parseCastParam(search.cast)} initialWorld={search.world} initialBand={search.band} initialIdea={search.idea} initialGenerationMode={search.generation_mode} initialAuthoringVersion={search.authoring_version} />
}
